import React from 'react';
import { Box, Stack } from '@mui/material';
import { DictionaryIrregularVerb } from 'app/types';
import UICard from 'components/ui/UICard';
import UIFlex from 'components/ui/UIFlex';
import UIBox from 'components/ui/UIBox';
import UIText from 'components/ui/UIText';
import ProgressBar from 'components/ProgressBar';
import { hexToRgba } from 'components/cefrMeta';
import { FormType, getFormDescription, dieslova } from './patternSuperGroups';
import { aggregateByForm } from './aggregations';
import { Selection } from './urlState';
import DrillHeader from './DrillHeader';
import IrregularVerbsTable from './IrregularVerbsTable';

// Matches the /wordset row accent for irregular-verb so the card frame on
// the inside page continues the brand of the row the user just clicked.
const FORMS_ACCENT = '#3B2C93';

const StatPill: React.FC<{ n: number; u: string }> = ({ n, u }) => (
  <UIBox
    sx={{
      alignItems: 'baseline',
      bgcolor: 'background.default',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 999,
      display: 'inline-flex',
      fontSize: 13,
      gap: 0.6,
      px: 1.4,
      py: 0.6,
      whiteSpace: 'nowrap',
    }}
  >
    <UIText component={'b'} sx={{ color: 'text.primary', fontWeight: 800 }}>
      {n}
    </UIText>
    <UIText component={'span'} color={'primary.subtitle'} sx={{ fontSize: 13 }}>
      {u}
    </UIText>
  </UIBox>
);

interface Props {
  verbs: readonly DictionaryIrregularVerb[];
  selection: Selection | null;
  onSelectForm: (form: FormType) => void;
  onClearSelection: () => void;
}

const FORMS: FormType[] = ['AAA', 'ABB', 'ABA', 'ABC'];

const FormsMode: React.FC<Props> = ({
  verbs,
  selection,
  onSelectForm,
  onClearSelection,
}) => {
  if (selection?.kind === 'form') {
    const filtered = verbs.filter((v) => v.formType === selection.value);
    return (
      <Box>
        <DrillHeader
          backLabel="До списку форм"
          title={`Форма ${selection.value}`}
          count={filtered.length}
          onBack={onClearSelection}
        />
        <IrregularVerbsTable verbs={filtered} />
      </Box>
    );
  }

  const agg = aggregateByForm(verbs);

  return (
    <Box>
      <Box sx={{ mb: 2, fontSize: 14, color: 'text.secondary' }}>
        Чотири типи зміни форм. Натисни блок, щоб побачити список дієслів.
      </Box>
      <Stack spacing={1.5}>
        {FORMS.map((f) => {
          const a = agg.get(f) ?? { total: 0, learned: 0 };
          const started = a.learned > 0;
          const description = getFormDescription(f);

          return (
            <UICard
              key={f}
              onClick={() => onSelectForm(f)}
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                p: 0,
                transition: 'border-color .16s ease',
                '&:hover': {
                  borderColor: FORMS_ACCENT,
                  bgcolor: hexToRgba(FORMS_ACCENT, 0.04),
                },
                '&:hover .vfc-arrow': { transform: 'translateX(4px)' },
              }}
            >
              <UIFlex
                ai={'center'}
                sx={{
                  flexWrap: 'nowrap',
                  gap: { xs: 1.75, md: 2.5 },
                  p: { xs: '14px', md: '16px 20px' },
                }}
              >
                {/* Form-code thumbnail — square with the AAA/ABB/… label */}
                <UIBox
                  sx={{
                    alignItems: 'center',
                    bgcolor: hexToRgba(FORMS_ACCENT, 0.1),
                    borderRadius: 2,
                    display: 'flex',
                    flex: 'none',
                    height: { xs: 60, md: 96 },
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                    width: { xs: 60, md: 96 },
                  }}
                >
                  <UIText
                    sx={{
                      color: FORMS_ACCENT,
                      fontSize: { xs: 20, md: 30 },
                      fontWeight: 800,
                      letterSpacing: '0.04em',
                      lineHeight: 1,
                    }}
                  >
                    {f}
                  </UIText>
                </UIBox>

                {/* Content */}
                <UIBox sx={{ flex: 1, minWidth: 0 }}>
                  <UIText
                    sx={{
                      // Code eyebrow ("Форма AAA") drops to default text —
                      // same en-black / ua-accent rule CategoryCard uses
                      // on /wordset/level-based.
                      color: 'text.primary',
                      fontSize: { xs: 10, md: 11 },
                      fontWeight: 800,
                      letterSpacing: '0.09em',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {`Форма ${f}`}
                  </UIText>
                  <UIText
                    variant={'h3'}
                    sx={{
                      color: FORMS_ACCENT,
                      fontSize: { xs: 17, md: 21 },
                      mb: 0.6,
                    }}
                  >
                    {description}
                  </UIText>
                  <UIText
                    color={'primary.subtitle'}
                    sx={{
                      display: '-webkit-box',
                      fontSize: { xs: 13, md: 14 },
                      mb: 1.25,
                      overflow: 'hidden',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: { xs: 2, md: 1 },
                    }}
                  >
                    {`Тип зміни форм ${f} — три форми дієслова за відповідним патерном.`}
                  </UIText>
                  <UIFlex jc={'flex-start'} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <StatPill n={a.total} u={dieslova(a.total)} />
                    {a.learned > 0 && (
                      <StatPill n={a.learned} u={'вивчено'} />
                    )}
                  </UIFlex>
                </UIBox>

                {/* Right cluster: progress + CTA — desktop only */}
                <UIBox
                  sx={{
                    alignItems: 'stretch',
                    display: { xs: 'none', md: 'flex' },
                    flex: 'none',
                    flexDirection: 'column',
                    gap: 1.5,
                    width: 250,
                  }}
                >
                  {started && (
                    <ProgressBar
                      addedValue={a.total}
                      learnedValue={a.learned}
                      totalValue={a.total}
                      minWidth={'100%'}
                      maxWidth={'100%'}
                      width={'100%'}
                    />
                  )}
                  <UIBox
                    sx={{
                      alignItems: 'center',
                      color: FORMS_ACCENT,
                      display: 'inline-flex',
                      fontSize: 15,
                      fontWeight: 700,
                      gap: 0.5,
                      justifyContent: 'flex-end',
                    }}
                  >
                    {'Відкрити'}
                    <UIBox
                      className={'vfc-arrow'}
                      component={'span'}
                      sx={{
                        display: 'inline-block',
                        transition:
                          'transform .25s cubic-bezier(0.22,1,0.36,1)',
                      }}
                    >
                      {'→'}
                    </UIBox>
                  </UIBox>
                </UIBox>
              </UIFlex>
            </UICard>
          );
        })}
      </Stack>
    </Box>
  );
};

export default FormsMode;
